'use client';

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'default' | 'success' | 'destructive' | 'warning';

interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
}

interface ToastContextValue {
  toast: (options: Omit<Toast, 'id'>) => { id: string; dismiss: () => void };
  dismiss: (toastId: string) => void;
  toasts: Toast[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Default / success toasts are green; destructive red; warning amber (info). */
function resolveToastVariant(variant?: ToastVariant): ToastVariant {
  if (!variant || variant === 'default') {
    return 'success';
  }
  return variant;
}

function toastShellClass(variant: ToastVariant): string {
  switch (variant) {
    case 'destructive':
      return cn(
        'border-red-300/90 bg-red-50 text-red-950',
        'dark:border-red-800/70 dark:bg-red-950/95 dark:text-red-50'
      );
    case 'warning':
      return cn(
        'border-amber-300/90 bg-amber-50 text-amber-950',
        'dark:border-amber-800/70 dark:bg-amber-950/95 dark:text-amber-50'
      );
    case 'success':
    default:
      return cn(
        'border-emerald-300/90 bg-emerald-50 text-emerald-950',
        'dark:border-emerald-800/70 dark:bg-emerald-950/95 dark:text-emerald-50'
      );
  }
}

function toastDescriptionClass(variant: ToastVariant): string {
  switch (variant) {
    case 'destructive':
      return 'text-red-800/90 dark:text-red-100/90';
    case 'warning':
      return 'text-amber-900/90 dark:text-amber-100/90';
    case 'success':
    default:
      return 'text-emerald-900/90 dark:text-emerald-100/90';
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((toastId: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  const toast = useCallback(
    ({
      title,
      description,
      variant = 'success',
    }: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).slice(2, 11);
      const newToast: Toast = { id, title, description, variant };

      setToasts((prev) => [...prev, newToast]);

      const timeoutId = window.setTimeout(() => {
        dismiss(id);
      }, 5000);

      return {
        id,
        dismiss: () => {
          window.clearTimeout(timeoutId);
          dismiss(id);
        },
      };
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ toast, dismiss, toasts }}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2 p-0 sm:p-0"
        aria-live="polite"
        aria-relevant="additions"
      >
        {toasts.map((t) => {
          const resolved = resolveToastVariant(t.variant);
          return (
            <div
              key={t.id}
              role="status"
              className={cn(
                'pointer-events-auto animate-in fade-in-0 slide-in-from-bottom-2 rounded-lg border px-4 py-3 shadow-lg duration-200',
                toastShellClass(resolved)
              )}
            >
              {t.title ? (
                <div className="text-sm font-semibold leading-snug">{t.title}</div>
              ) : null}
              {t.description ? (
                <div
                  className={cn(
                    'text-sm leading-snug',
                    t.title ? 'mt-0.5' : '',
                    toastDescriptionClass(resolved)
                  )}
                >
                  {t.description}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
